import { useDisclosure } from '@chakra-ui/react';
import {
  useListingComparableSetForBoaTokenQuery,
  useListingComparableSetForBuyerOfferAnalysisQuery,
  useUpdateListingComparableSetForBuyerOfferAnalysisMutation,
} from '@client/graphql/__generated__/main-operations';
import {
  BuyerOfferAnalysisFragment,
  UpdateListingComparableInput,
} from '@client/graphql/__generated__/types';
import isEqualWith from 'lodash/isEqualWith';
import { useEffect, useMemo, useState } from 'react';
import { ComparableState } from '~/apps/consumer/pages/ListingSetup/Comparables/ComparablesPage';

export const useBuyerOfferAnalysisComps = ({
  updateIndigoOfferRecs,
  buyerOfferAnalysis,
  isViewOnly,
  token,
}: {
  updateIndigoOfferRecs: () => Promise<void>;
  buyerOfferAnalysis?: BuyerOfferAnalysisFragment;
  isViewOnly?: boolean;
  token?: string;
}) => {
  const [initialSelectedComp, setInitialSelectedComp] =
    useState<ComparableState>();
  const [comps, setComps] = useState<ComparableState[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isCompsUpdated, setIsCompsUpdated] = useState(false);
  const [updateComparableSet] =
    useUpdateListingComparableSetForBuyerOfferAnalysisMutation();

  const {
    data: comparableSetData,
    loading: comparableSetLoading,
    refetch: refetchComparableSet,
  } = useListingComparableSetForBuyerOfferAnalysisQuery({
    variables: {
      input: {
        buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
      },
    },
    skip: !buyerOfferAnalysis || !!token,
  });

  const { data: tokenComparableSetData, loading: tokenComparableSetLoading } =
    useListingComparableSetForBoaTokenQuery({
      variables: {
        input: {
          token: token || '',
          buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
        },
      },
      skip: !token || !buyerOfferAnalysis,
    });

  const comparableSet = useMemo(() => {
    if (token) {
      return tokenComparableSetData?.listingComparableSetForBOAToken;
    }

    return comparableSetData?.listingComparableSetForBuyerOfferAnalysis;
  }, [token, comparableSetData, tokenComparableSetData]);

  const { isOpen: isEditCompsModalOpen, onOpen, onClose } = useDisclosure();

  const updateComps = async (comparables: UpdateListingComparableInput[]) => {
    if (isViewOnly) {
      return;
    }

    await updateComparableSet({
      variables: {
        input: {
          comparables,
          buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
        },
      },
    });
    await refetchComparableSet();
  };

  const onEditCompsModalOpen = (comp?: ComparableState) => {
    comp && setInitialSelectedComp(comp);
    onOpen();
  };

  const onEditCompsModalClose = () => {
    if (isViewOnly) {
      return;
    }

    if (isCompsUpdated) {
      void updateIndigoOfferRecs();
      setIsCompsUpdated(false);
    }
    onClose();
  };

  const originalOrderedComps = useMemo(() => {
    if (comparableSet?.listingComparables) {
      return comparableSet.listingComparables
        ?.toSorted((a, b) => a.order - b.order)
        ?.map((comp) => ({
          mlsListing: comp.mlsListing,
        }));
    }

    return [];
  }, [comparableSet]);

  // sets the comps on initial page load
  useEffect(() => {
    if (!isDirty && originalOrderedComps?.length) {
      setComps(originalOrderedComps || []);
    }
  }, [isDirty, originalOrderedComps]);

  // updates the dirty state based on changes to the comps compared to the original ordered comps
  useEffect(() => {
    if (isViewOnly) {
      return;
    }

    const comparator = (a: ComparableState[], b: ComparableState[]) =>
      a.length === b.length &&
      a.every(
        (comparable, index) =>
          comparable.mlsListing.id === b[index].mlsListing.id
      );
    if (!isEqualWith(comps, originalOrderedComps, comparator)) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [comps]); // eslint-disable-line react-hooks/exhaustive-deps

  // updates the comps in the database when the comps are dirty
  useEffect(() => {
    if (isDirty && !isViewOnly) {
      const input = comps.map((comp, index) => ({
        mlsListingId: comp.mlsListing.id,
        order: index + 1,
      }));
      void updateComps(input);
      setIsCompsUpdated(true);
    }
  }, [isDirty, comps, isViewOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    comps,
    setComps,
    isDirty,
    setIsDirty,
    isCompsUpdated,
    setIsCompsUpdated,
    updateComps,
    onEditCompsModalOpen,
    onEditCompsModalClose,
    isEditCompsModalOpen,
    comparableSetLoading: tokenComparableSetLoading || comparableSetLoading,
    refetchComparableSet,
    initialSelectedComp,
    setInitialSelectedComp,
    comparableSet,
  };
};
